<template>
  <div class="nortic-tabs">
    <div
      class="tabs-wrapper"
      :class="{sticky: stickyHeader}"
    >
      <ul
        ref="tabs"
      >
        <template
          v-for="{ title, subtitle, icon, badge, data, index, isDisabled, validatorResult, isActive, to, replace, append, exact } in tabObjects"
        >
          <RouterLink
            v-if="isRouterLink(to, isDisabled)"
            :key="index"
            :to="to"
            active-class="tab-active"
            :class="{ danger: validatorResult.hasError, warn: validatorResult.hasWarning }"
            tag="li"
            :tabindex="index"
            :exact="exact"
            :replace="replace"
            :append="append"
          >
            <span class="tab">
              <slot
                name="tab"
                :tab="{ title, subtitle, icon, badge, data, index, isDisabled, isActive }"
              >
                <div class="tab-content">
                  <span
                    v-if="icon"
                    class="icon"
                  >
                    <SvgIcon
                      :data="icon"
                      height="1.5em"
                      width="1.5em"
                    />
                  </span>
                  <span class="text">
                    {{ title }}
                    <NorticBadge
                      v-if="badge"
                      class="nortic-badge"
                      :theme-type="badge.themeType"
                    >{{ badge.value }}</NorticBadge>
                  </span>
                  <span
                    v-if="subtitle"
                    class="sub-text"
                  >{{ subtitle }}</span>
                </div>
              </slot>
            </span>
          </RouterLink>
          <li
            v-else
            :key="index"
            :class="{ 'tab-disabled': isDisabled, 'tab-active': isActive, danger: validatorResult.hasError, warn: validatorResult.hasWarning }"
            :tabindex="index"
            @click="select(index)"
          >
            <span class="tab">
              <slot
                name="tab"
                :tab="{title, subtitle, icon, badge, data, index}"
              >
                <div class="tab-content">
                  <span
                    v-if="icon"
                    class="icon"
                  >
                    <SvgIcon
                      :data="icon"
                      height="1.5em"
                      width="1.5em"
                    />
                  </span>
                  <span class="text">
                    {{ title }}
                    <NorticBadge
                      v-if="badge"
                      class="nortic-badge"
                      :theme-type="badge.themeType"
                    >{{ badge.value }}</NorticBadge>
                  </span>

                  <span
                    v-if="subtitle"
                    class="sub-text"
                  >{{ subtitle }}</span>
                </div>
              </slot>
            </span>
          </li>
        </template>
        <div
          class="active-border"
          :class="{
            danger: selectedTabObject.validatorResult && selectedTabObject.validatorResult.hasError,
            warn: selectedTabObject.validatorResult && selectedTabObject.validatorResult.hasWarning
          }"
          :style="bottomBorder"
        />
      </ul>
    </div>
    <div>
      <slot>
        <NorticTab
          v-for="(tab, index) in tabs"
          :key="index"
          :title="tab.title"
          :subtitle="tab.subtitle"
          :content-text="tab.contentText"
          :is-disabled="tab.isDisabled"
        />
      </slot>
    </div>
  </div>
</template>

<script>
import NorticTab from '@/components/common/navigation/tabs/NorticTab.vue';
import NorticBadge from '@/components/common/misc/badge/NorticBadge.vue';

export default {
  name: 'NorticTabs',
  components: { NorticBadge, NorticTab },
  props: {
    tabs: {
      type: Object,
      default: () => {},
    },
    selectedIndex: {
      type: Number,
      default: 0,
    },
    syncRoute: {
      type: Boolean,
      default: false,
    },
    stickyHeader: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      tabObjects: [],
      selectedIndexInner: this.selectedIndex,
      borderWidth: 0,
      borderOffset: 0,
      shouldTransition: true,
      selectedTabObject: {},
    };
  },
  computed: {
    bottomBorder() {
      return {
        width: `${this.borderWidth}px`,
        transform: `translateX(${this.borderOffset}px)`,
      };
    },
  },
  watch: {
    selectedIndex(newIndex) {
      this.select(newIndex);
    },
    selectedIndexInner(newTabIndex, oldTabIndex) {
      this.$emit('tab-change', this.tabObjects[newTabIndex], this.tabObjects[oldTabIndex]);
    },
    $route() {
      if (this.syncRoute) {
        this.setSelectedStyleData();
        this.setIndexFromActiveClass();
      }
    },
  },
  mounted() {
    window.addEventListener('resize', this.resizeEvent);

    this.$nextTick(() => {
      setTimeout(() => {
        this.setSelectedStyleData();
        if (this.syncRoute) {
          this.setIndexFromActiveClass();
        }
      }, 100);
    });
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.resizeEvent);
  },
  methods: {
    select(index) {
      if (!this.tabObjects[index].isDisabled) {
        this.selectedIndexInner = index;
        this.setSelectedStyleData();
      }
    },
    setSelectedStyleData() {
      this.$nextTick().then(() => {
        const el = this.$refs.tabs.querySelector('.tab-active');
        if (el) {
          this.borderWidth = el.clientWidth;
          this.borderOffset = el.offsetLeft - this.$refs.tabs.scrollLeft;
        } else {
          this.borderWidth = 0;
          this.borderOffset = 0;
        }
      });
    },
    resizeEvent() {
      this.shouldTransition = false;
      this.setSelectedStyleData();
      this.shouldTransition = true;
    },
    setIndexFromActiveClass() {
      this.$nextTick(() => {
        const el = this.$refs.tabs.querySelector('.tab-active');
        if (el) {
          if (el.tabIndex !== this.selectedIndexInner) {
            this.selectedIndexInner = el.tabIndex;
          }
        } else {
          this.selectedIndexInner = -1;
        }
      });
    },
    isRouterLink(to, isDisabled) {
      return this.syncRoute && to && !isDisabled;
    },
  },
};
</script>

<style lang="scss" scoped>
  .nortic-tabs {
    display: grid;

    .tabs-wrapper {
      &.sticky {
        position: sticky;
        top: 0;
      }

      overflow: auto;

      ul {
        padding: 0 5px;
        display: flex;
        flex-direction: row;
        margin: 0;
        position: relative;

        li {
          &.tab-active {
            color: var(--primary-text-alt-color-hex);
          }

          &.tab-disabled {
            background: var(--primary-variant-bg-hex);
            opacity: 0.3;
            cursor: default;
          }

          &:not(.tab-active):hover {
            &.warn {
              color: var(--warn-variant-text-alt-color-hex);
            }

            &.danger {
              color: var(--danger-variant-text-alt-color-hex);
            }

            color: var(--primary-text-alt-color-hex);
          }

          &.warn {
            color: var(--warn-text-alt-color-hex);
            border-color: var(--warn-variant-bg-hex);
          }

          &.danger {
            color: var(--danger-text-alt-color-hex);
            border-color: var(--danger-variant-bg-hex);
          }

          font-size: 13px;
          transition: color 0.4s;
          color: var(--primary-text-hex);
          background: var(--background-bg-hex);
          padding: 10px 20px;
          list-style-type: none;
          text-align: center;
          cursor: pointer;
          outline-width: 0;
          border-bottom: 1px solid var(--border-color-hex);

          .tab {
            position: relative;
            z-index: 10;
            white-space: nowrap;

            .tab-content {
              position: relative;
              z-index: 10;
              display: grid;
              height: 100%;
              align-items: end;

              .text {
                white-space: nowrap;

                .nortic-badge {
                  margin-left: 0.2em;
                }
              }

              .sub-text {
                color: var(--primary-text-discrete-hex);
                font-weight: 500;
                font-size: 12px;
                white-space: nowrap;
              }

              .icon {
                font-size: 1.5em;
              }
            }
          }
        }
      }

      .active-border {
        &.has-error {
          border-color: var(--danger-text-alt-color-hex);
        }

        z-index: 1;
        content: '';
        height: calc(100% - 2px);
        background: var(--primary-bg-hex);
        position: absolute;
        left: 0;
        bottom: 0;
        border-top: 2px solid var(--primary-text-alt-color-hex);
        transition: width 0.2s cubic-bezier(0.445, 0.05, 0.55, 0.95), transform 0.2s cubic-bezier(0.445, 0.05, 0.55, 0.95);
      }
    }
  }
</style>
